import * as React from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useCreateSnapshotViewMutation, useUpdateSnapshotViewMutation } from "@/modules/snapshots/snapshotViews.api";
import type { SnapshotView, SnapshotViewConfiguration } from "@/modules/snapshots/snapshotViews.types";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { formatSnapshotGridLocalDate } from "@/modules/snapshots/grid/gridQueryPayload";

interface FormValues {
  name: string;
  dateMode: "fixed" | "current_month" | "previous_month" | "current_quarter" | "current_year" | "last_30_days";
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  snapshotCollectionId: string;
  configuration: SnapshotViewConfiguration;
  snapshotView?: SnapshotView;
}

const resolvedDynamicDateRange = (
  dateRange: NonNullable<SnapshotViewConfiguration["date_range"]>,
  now = new Date(),
): NonNullable<SnapshotViewConfiguration["date_range"]> => {
  if (dateRange.mode === "fixed") return dateRange;

  const year = now.getFullYear();
  const month = now.getMonth();
  let startDate: Date;
  let endDate: Date;

  if (dateRange.preset === "last_30_days") {
    startDate = new Date(year, month, now.getDate() - 29);
    endDate = now;
  } else if (dateRange.preset === "current_year") {
    startDate = new Date(year, 0, 1);
    endDate = new Date(year, 11, 31);
  } else if (dateRange.preset === "current_quarter") {
    const quarterStartMonth = Math.floor(month / 3) * 3;
    startDate = new Date(year, quarterStartMonth, 1);
    endDate = new Date(year, quarterStartMonth + 3, 0);
  } else {
    const targetMonth = dateRange.preset === "previous_month" ? month - 1 : month;
    startDate = new Date(year, targetMonth, 1);
    endDate = new Date(year, targetMonth + 1, 0);
  }

  return {
    mode: "fixed",
    field_key: dateRange.field_key,
    start_date: formatSnapshotGridLocalDate(startDate),
    end_date: formatSnapshotGridLocalDate(endDate),
    preset: null,
  };
};

export const SaveSnapshotViewDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  snapshotCollectionId,
  configuration,
  snapshotView,
}) => {
  const [createView, createState] = useCreateSnapshotViewMutation();
  const [updateView, updateState] = useUpdateSnapshotViewMutation();
  const user = useAuthenticatedUser();
  const canEdit = !snapshotView || snapshotView.owner_id === user.id;
  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    defaultValues: {
      name: snapshotView?.name ?? "",
      dateMode: configuration.date_range?.mode === "dynamic" ? configuration.date_range.preset : "fixed",
    },
  });

  React.useEffect(() => {
    if (open) {
      reset({
        name: snapshotView?.name ?? "",
        dateMode: configuration.date_range?.mode === "dynamic" ? configuration.date_range.preset : "fixed",
      });
    }
  }, [configuration.date_range, open, reset, snapshotView?.name]);

  const configurationForForm = (dateMode: FormValues["dateMode"]): SnapshotViewConfiguration =>
    dateMode !== "fixed" && configuration.date_range
      ? {
          ...configuration,
          date_range: {
            mode: "dynamic",
            field_key: configuration.date_range.field_key,
            start_date: null,
            end_date: null,
            preset: dateMode,
          },
        }
      : dateMode === "fixed" && configuration.date_range
        ? { ...configuration, date_range: resolvedDynamicDateRange(configuration.date_range) }
        : configuration;

  const saveAsNew = async ({ name, dateMode }: FormValues) => {
    try {
      await createView({
        name,
        snapshot_collection_id: snapshotCollectionId,
        configuration: configurationForForm(dateMode),
      }).unwrap();
      toast.success("View saved");
      onOpenChange(false);
    } catch {
      toast.error("Unable to save view");
    }
  };

  const saveChanges = async ({ name, dateMode }: FormValues) => {
    if (!snapshotView) return;
    try {
      await updateView({
        id: snapshotView.id,
        revision: snapshotView.revision,
        name,
        configuration: configurationForForm(dateMode),
      }).unwrap();
      toast.success("View changes saved");
      onOpenChange(false);
    } catch {
      toast.error("The view changed elsewhere. Reload it or save as a new view.");
    }
  };

  const isSaving = createState.isLoading || updateState.isLoading;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{snapshotView ? "Save view" : "Save View"}</DialogTitle>
          <DialogDescription>
            Saves {Object.keys(configuration.column_filters).length} active filters,{" "}
            {configuration.hidden_column_keys.length} hidden columns and the current column order. Data remains live.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit(saveAsNew)}>
          <label className="block text-sm font-medium text-gray-800">
            View name
            <input
              {...register("name", { required: "Enter a view name", maxLength: 200 })}
              className="mt-1 h-10 w-full rounded-md border border-gray-300 px-3"
              autoFocus
            />
          </label>
          {formState.errors.name && <p className="text-sm text-red-600">{formState.errors.name.message}</p>}
          {configuration.date_range && (
            <label className="block text-sm font-medium text-gray-800">
              Date range behavior
              <select {...register("dateMode")} className="mt-1 h-10 w-full rounded-md border border-gray-300 px-3">
                <option value="fixed">Keep fixed dates</option>
                <option value="current_month">Current month</option>
                <option value="previous_month">Previous month</option>
                <option value="current_quarter">Current quarter</option>
                <option value="current_year">Current year</option>
                <option value="last_30_days">Last 30 days</option>
              </select>
            </label>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {snapshotView && canEdit && (
              <Button type="button" variant="outline" disabled={isSaving} onClick={handleSubmit(saveChanges)}>
                Save changes
              </Button>
            )}
            <Button type="submit" disabled={isSaving}>
              {snapshotView ? "Save as new view" : "Save View"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
